import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Row,
  Table,
} from 'react-bootstrap';
import applicationDetailSignal from '../ApplicationDetail.signal';
import currencyOptions from 'components/global/Constant/currencyOptions';
import edoFundingDisbursementSignal from 'components/global/Disbursement/Disbursement.signals';
import DisbursementAddManualTransactionModal from 'components/global/Disbursement/DisbursementAddManualTransactionModal';
import { fetchApplication } from '../ApplicationDetail.helpers';
import api from 'api/api';
import alertSignal from 'signals/Alert.signal';
import loaderSignal from 'signals/Loader.signal';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import dateOptions from 'components/global/Constant/dateOptions';
import $appSettings from 'signals/AppSettings.signal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import FilePreviewButton from 'components/global/utils/FilesAndDocuments/PreviewButton/FilePreviewButton';
import AddAttachmentsButton from 'components/global/AddAttachmentsButton';
import { uploadFilesToStorage } from 'libs/functions/global.functions';

const DISBURSEMENT_TABLE_HEADERS = [
  { key: 'expand', title: '' },
  { key: 'id', title: 'ID' },
  { key: 'amount', title: 'Amount' },
  { key: 'fundType', title: 'Fund Type' },
  { key: 'kind', title: 'Kind' },
  { key: 'premiumRate', title: 'Premium Rate' },
  { key: 'status', title: 'Status' },
  { key: 'transactions', title: 'Transactions' },
  { key: 'created', title: 'Created Date' },
  { key: 'premiumDate', title: 'Premium Date' },
  { key: 'actions', title: 'Actions' },
];

const TRANSACTION_TABLE_HEADERS = [
  { key: 'id', title: 'ID' },
  { key: 'amount', title: 'Amount' },
  { key: 'created', title: 'Created' },
  { key: 'memo', title: 'Memo' },
  { key: 'attachments', title: 'Attachments' },
  { key: 'actions', title: 'Actions' },
];

function formatDate(val) {
  if (val == null) return '-';
  return new Date(val).toLocaleDateString('en-US', dateOptions.dateFormatted);
}

function formatDateTime(val) {
  if (val == null) return '-';
  return new Date(val).toLocaleString('en-US', dateOptions.dateTimeFormatted);
}

function readDisbursementMeta(disbursement) {
  const meta =
    disbursement?.meta && typeof disbursement.meta === 'object'
      ? disbursement.meta
      : {};
  const importBlob =
    meta?.cpcfaImportBlob && typeof meta.cpcfaImportBlob === 'object'
      ? meta.cpcfaImportBlob
      : disbursement?.cpcfaImportBlob && typeof disbursement.cpcfaImportBlob === 'object'
        ? disbursement.cpcfaImportBlob
        : {};

  return { ...meta, ...importBlob };
}

function excelSerialDateToJsDate(serial) {
  const n = Number(serial);
  if (Number.isNaN(n)) return null;
  const epoch = Date.UTC(1899, 11, 30);
  return new Date(epoch + n * 24 * 60 * 60 * 1000);
}

function readPremiumDate(disbursement) {
  if (disbursement?.premiumDate != null && disbursement.premiumDate !== '') {
    const fromCol = new Date(disbursement.premiumDate);
    if (!Number.isNaN(fromCol.getTime())) return fromCol;
  }
  const meta = readDisbursementMeta(disbursement);
  const raw = meta.FND_DATE ?? meta.fndDate ?? meta.fnd_date ?? null;
  if (raw == null || raw === '') return null;

  // FRED imports often store FND_DATE as Excel serial number.
  if (typeof raw === 'number' || /^\d+(\.\d+)?$/.test(String(raw))) {
    const n = Number(raw);
    if (n > 1000) return excelSerialDateToJsDate(n);
  }

  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d;
  return null;
}

function readPremiumRate(disbursement) {
  const meta = readDisbursementMeta(disbursement);
  const raw = meta.FND_RATE ?? meta.fndRate ?? meta.fnd_rate ?? null;
  if (raw == null || raw === '') return '-';
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw);
  return `${n}%`;
}

function readPremiumRateValue(disbursement) {
  const meta = readDisbursementMeta(disbursement);
  const raw = meta.FND_RATE ?? meta.fndRate ?? meta.fnd_rate ?? null;
  if (raw == null || raw === '') return '';
  return String(raw);
}

function parseCurrency(value) {
  if (!value || String(value).trim() === '') return null;
  const cleaned = String(value).replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

function toIsoDateTimeOrNull(dateString) {
  if (!dateString) return null;
  const parsed = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

/** YYYY-MM-DD in local time — matches formatDate() and toIsoDateTimeOrNull() (local midnight). */
function toLocalYmd(dateValue) {
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function secondsToTimeInputValue(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeInputValueToSeconds(value) {
  if (!value) return 0;
  const parts = value.split(':').map(Number);
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60;
}

function findTransactionParent(disbursementsList, transactionId) {
  for (const d of disbursementsList || []) {
    const inDisb = (d.transactions || []).some((t) => t.id === transactionId);
    if (inDisb) return { parent: d, transactions: d.transactions || [] };
    for (const r of d.reimbursements || []) {
      const inReimb = (r.transactions || []).some((t) => t.id === transactionId);
      if (inReimb) return { parent: r, transactions: r.transactions || [] };
    }
  }
  return null;
}

const ApplicationDisbursementsTab = () => {
  const {
    id: applicationId,
    disbursements: disbursementsList = [],
    status: applicationStatus,
  } = applicationDetailSignal.value;

  const { APPLICATION_STATUS } = $appSettings.value.constants;
  const isDraftApplication = applicationStatus === APPLICATION_STATUS.inProgress;

  const [addDisbursementShow, setAddDisbursementShow] = useState(false);
  const [editDisbursement, setEditDisbursement] = useState(null);
  const [addDisbursementAmount, setAddDisbursementAmount] = useState('');
  const [addDisbursementDueDate, setAddDisbursementDueDate] = useState('');
  const [addDisbursementFundType, setAddDisbursementFundType] = useState('');
  const [addDisbursementPremiumRate, setAddDisbursementPremiumRate] = useState('');
  const [editTransaction, setEditTransaction] = useState(null);
  const [editTransactionAmount, setEditTransactionAmount] = useState('');
  const [editTransactionMemo, setEditTransactionMemo] = useState('');
  const [editTransactionDate, setEditTransactionDate] = useState('');
  const [editTransactionTime, setEditTransactionTime] = useState(0);
  const [expandedDisbursementIds, setExpandedDisbursementIds] = useState(new Set());
  const [expandedReimbursementIds, setExpandedReimbursementIds] = useState(new Set());
  const [addDisbursementAmountFocused, setAddDisbursementAmountFocused] = useState(false);
  const [addDisbursementAmountRaw, setAddDisbursementAmountRaw] = useState('');
  const [editTransactionAmountFocused, setEditTransactionAmountFocused] = useState(false);
  const [editTransactionAmountRaw, setEditTransactionAmountRaw] = useState('');
  const [editTransactionAttachments, setEditTransactionAttachments] = useState([]);

  const fundTypeOptions =
    $appSettings.value.constants?.DISBURSEMENT_FUND_TYPE_OPTIONS ?? [];

  useEffect(() => {
    if (isDraftApplication && addDisbursementShow && !editDisbursement) {
      setAddDisbursementShow(false);
      setAddDisbursementAmountFocused(false);
      setAddDisbursementAmountRaw('');
    }
  }, [isDraftApplication, addDisbursementShow, editDisbursement]);

  const openAddDisbursementModal = () => {
    if (isDraftApplication) return;
    setEditDisbursement(null);
    setAddDisbursementAmount('');
    setAddDisbursementDueDate('');
    setAddDisbursementFundType('');
    setAddDisbursementPremiumRate('');
    setAddDisbursementAmountFocused(false);
    setAddDisbursementAmountRaw('');
    setAddDisbursementShow(true);
  };

  const openEditDisbursementModal = (disbursement) => {
    setEditDisbursement(disbursement);
    setAddDisbursementAmount(String(disbursement?.amount ?? ''));
    const premiumDate = readPremiumDate(disbursement);
    setAddDisbursementDueDate(premiumDate ? toLocalYmd(premiumDate) : '');
    setAddDisbursementFundType(
      disbursement?.meta?.fundTypeCode ??
        fundTypeOptions.find((o) => o.label === disbursement?.meta?.fundType)?.code ??
        ''
    );
    setAddDisbursementPremiumRate(readPremiumRateValue(disbursement));
    setAddDisbursementAmountFocused(false);
    setAddDisbursementAmountRaw('');
    setAddDisbursementShow(true);
  };

  const hideAddDisbursementModal = () => {
    setAddDisbursementShow(false);
  };

  const resetAddDisbursementForm = () => {
    setEditDisbursement(null);
    setAddDisbursementAmount('');
    setAddDisbursementDueDate('');
    setAddDisbursementFundType('');
    setAddDisbursementPremiumRate('');
    setAddDisbursementAmountFocused(false);
    setAddDisbursementAmountRaw('');
  };

  const submitAddDisbursement = async () => {
    if (!editDisbursement && isDraftApplication) return;
    const amount = Number(addDisbursementAmount);
    if (!applicationId || Number.isNaN(amount) || amount <= 0) {
      alertSignal.update({
        type: 'notification',
        message: 'Please enter a valid amount.',
        variant: 'danger',
      });
      return;
    }
    if (!addDisbursementFundType) {
      alertSignal.update({
        type: 'notification',
        message: 'Fund type is required.',
        variant: 'danger',
      });
      return;
    }
    const isEdit = !!editDisbursement;
    const premiumRateRaw = String(addDisbursementPremiumRate ?? '').replace(/[%\s]/g, '').trim();
    const premiumRateParsed = premiumRateRaw === '' ? null : Number(premiumRateRaw);
    if (premiumRateRaw !== '' && Number.isNaN(premiumRateParsed)) {
      alertSignal.update({
        type: 'notification',
        message: 'Please enter a valid premium rate.',
        variant: 'danger',
      });
      return;
    }
    if (isEdit && editDisbursement?.transactions?.length > 0) {
      const transactionsSum = editDisbursement.transactions
        .filter((t) => t.reimbursementId == null)
        .reduce((acc, t) => acc + (t.amount ?? 0), 0);
      if (amount < transactionsSum) {
        alertSignal.update({
          type: 'notification',
          message: 'Transaction amounts cannot exceed the disbursement amount. Reduce or remove transactions before reducing the disbursement amount.',
          variant: 'danger',
        });
        return;
      }
    }
    loaderSignal.update({
      isContentLoading: true,
      message: isEdit ? 'Updating disbursement...' : 'Adding disbursement...',
    });
    const premiumDateIso = toIsoDateTimeOrNull(addDisbursementDueDate);
    try {
      if (isEdit) {
        const meta = { ...(editDisbursement?.meta && typeof editDisbursement.meta === 'object' ? editDisbursement.meta : {}) };
        if (addDisbursementFundType) {
          meta.fundType = fundTypeOptions.find((o) => o.code === addDisbursementFundType)?.label ?? addDisbursementFundType;
          meta.fundTypeCode = addDisbursementFundType;
        } else {
          delete meta.fundType;
          delete meta.fundTypeCode;
        }
        if (premiumRateRaw !== '' && premiumRateParsed != null) {
          meta.fundRate = premiumRateParsed;
          meta.fndRate = premiumRateParsed;
          meta.FND_RATE = premiumRateParsed;
        } else {
          delete meta.fundRate;
          delete meta.fndRate;
          delete meta.FND_RATE;
        }
        if (addDisbursementDueDate) {
          meta.FND_DATE = addDisbursementDueDate;
          meta.fndDate = addDisbursementDueDate;
        } else {
          delete meta.FND_DATE;
          delete meta.fndDate;
          delete meta.fnd_date;
        }
        if (!addDisbursementFundType) {
          alertSignal.update({
            type: 'notification',
            message: 'Fund type is required.',
            variant: 'danger',
          });
          return;
        }
        await api.patch({
          path: `/v2/disbursements/${editDisbursement.id}`,
          body: {
            amount,
            premiumDate: premiumDateIso,
            meta,
          },
        });
      } else {
        await api.post({
          path: '/v2/disbursements',
          body: {
            data: {
              applicationId,
              amount,
              ...(premiumDateIso ? { premiumDate: premiumDateIso } : {}),
              ...(addDisbursementFundType
                ? {
                    fundType: fundTypeOptions.find((o) => o.code === addDisbursementFundType)?.label ?? addDisbursementFundType,
                    fundTypeCode: addDisbursementFundType,
                  }
                : {}),
              ...(premiumRateRaw !== '' && premiumRateParsed != null
                ? { fundRate: premiumRateParsed }
                : {}),
              kind: 'Disbursement',
            },
          },
        });
      }
      hideAddDisbursementModal();
      await fetchApplication(applicationId);
      alertSignal.update({
        type: 'notification',
        message: isEdit ? 'Disbursement updated.' : 'Disbursement added.',
        variant: 'success',
      });
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        message: error?.message ?? (isEdit ? 'Failed to update disbursement.' : 'Failed to add disbursement.'),
        variant: 'danger',
        error,
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const openEditTransactionModal = (transaction) => {
    setEditTransaction(transaction);
    setEditTransactionAmount(String(transaction?.amount ?? ''));
    setEditTransactionMemo(transaction?.memo ?? '');
    setEditTransactionAmountFocused(false);
    setEditTransactionAmountRaw('');
    setEditTransactionAttachments(
      (Array.isArray(transaction?.attachments) ? transaction.attachments : []).map(
        (a, i) => ({ ...a, _originalIndex: i })
      )
    );
    const td = transaction?.createdDate ?? transaction?.transferredDate;
    if (td) {
      const d = new Date(td);
      setEditTransactionDate(d.toISOString().split('T')[0]);
      setEditTransactionTime(d.getHours() * 3600 + d.getMinutes() * 60);
    } else {
      setEditTransactionDate('');
      setEditTransactionTime(0);
    }
  };

  const hideEditTransactionModal = () => {
    setEditTransaction(null);
  };

  const removeEditTransactionAttachment = (index) => {
    const attachment = editTransactionAttachments[index];
    const isExisting = attachment?.filePath;
    if (isExisting) {
      if (!window.confirm('Removing this attachment will delete the file. Are you sure?')) return;
    }
    setEditTransactionAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const addEditTransactionAttachments = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const newItems = Array.from(files).map((file) => ({
      name: file.name.split('.')[0],
      file,
    }));
    setEditTransactionAttachments((prev) => [...prev, ...newItems]);
  };

  const changeEditTransactionAttachmentName = (index, name) => {
    setEditTransactionAttachments((prev) =>
      prev.map((a, i) =>
        i === index
          ? { ...a, fileTitle: name, name: a.file ? name : (a.name ?? name) }
          : a
      )
    );
  };

  const resetEditTransactionForm = () => {
    setEditTransactionAmount('');
    setEditTransactionMemo('');
    setEditTransactionDate('');
    setEditTransactionTime(0);
    setEditTransactionAmountFocused(false);
    setEditTransactionAmountRaw('');
    setEditTransactionAttachments([]);
  };

  const submitEditTransaction = async () => {
    if (!editTransaction) return;
    const amount = Number(editTransactionAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      alertSignal.update({
        type: 'notification',
        message: 'Please enter a valid amount.',
        variant: 'danger',
      });
      return;
    }
    if (!editTransactionMemo?.trim()) {
      alertSignal.update({
        type: 'notification',
        message: 'Memo is required.',
        variant: 'danger',
      });
      return;
    }
    const parentInfo = findTransactionParent(disbursementsList, editTransaction.id);
    if (parentInfo) {
      const otherSum = parentInfo.transactions
        .filter((t) => t.id !== editTransaction.id)
        .reduce((acc, t) => acc + t.amount, 0);
      if (otherSum + amount > parentInfo.parent.amount) {
        alertSignal.update({
          type: 'notification',
          message: 'The total of all transactions cannot exceed the disbursement amount.',
          variant: 'danger',
        });
        return;
      }
    }
    const hours = Math.floor(editTransactionTime / 3600);
    const minutes = Math.floor((editTransactionTime % 3600) / 60);
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    const transferredDate = editTransactionDate ? `${editTransactionDate} ${timeStr}` : null;

    loaderSignal.update({ isContentLoading: true, message: 'Updating transaction...' });

    let attachmentsPayload = [];
    const newFiles = editTransactionAttachments.filter((a) => a.file);
    const existingAttachments = editTransactionAttachments.filter((a) => a.filePath);
    if (newFiles.length > 0) {
      const signedUrls = await uploadFilesToStorage(
        newFiles.map((a) => a.file),
        'transaction'
      );
      attachmentsPayload = [
        ...existingAttachments.map(({ _originalIndex, ...a }) => ({
          fileTitle: a.fileTitle ?? a.name ?? 'Attachment',
          filePath: a.filePath,
          fileType: a.fileType,
        })),
        ...newFiles.map((a, i) => ({
          fileTitle: a.fileTitle ?? a.name ?? a.file?.name?.split('.')[0] ?? 'Attachment',
          filePath: signedUrls[i],
          fileType: a.file.type,
        })),
      ];
    } else {
      attachmentsPayload = existingAttachments.map(({ _originalIndex, ...a }) => ({
        fileTitle: a.fileTitle ?? a.name ?? 'Attachment',
        filePath: a.filePath,
        fileType: a.fileType,
      }));
    }
    try {
      await api.patch({
        path: `/v2/transactions/${editTransaction.id}`,
        body: {
          amount,
          memo: editTransactionMemo.trim(),
          ...(transferredDate ? { transferredDate } : {}),
          attachments: attachmentsPayload,
        },
      });
      hideEditTransactionModal();
      await fetchApplication(applicationId);
      alertSignal.update({
        type: 'notification',
        message: 'Transaction updated.',
        variant: 'success',
      });
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        message: error?.message ?? 'Failed to update transaction.',
        variant: 'danger',
        error,
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const toggleDisbursementExpanded = (disbursementId) => {
    setExpandedDisbursementIds((prev) => {
      const next = new Set(prev);
      if (next.has(disbursementId)) next.delete(disbursementId);
      else next.add(disbursementId);
      return next;
    });
  };

  const toggleReimbursementExpanded = (reimbursementId) => {
    setExpandedReimbursementIds((prev) => {
      const next = new Set(prev);
      if (next.has(reimbursementId)) next.delete(reimbursementId);
      else next.add(reimbursementId);
      return next;
    });
  };

  const openAddTransactionModal = (disbursement) => {
    edoFundingDisbursementSignal.update({
      record: disbursement,
      createTransactionModalOpen: true,
      onTransactionCreatedCallback: async () => {
        if (applicationId) await fetchApplication(applicationId);
      },
    });
  };

  const handleDeleteDisbursement = async (disbursement) => {
    if (!applicationId) return;
    const transactionCount = disbursement?.transactions?.length ?? 0;
    if (transactionCount > 0) {
      alertSignal.update({
        type: 'notification',
        message: 'Cannot delete a disbursement that has related transactions. Delete all transactions first.',
        variant: 'danger',
      });
      return;
    }
    const reimbursementCount = disbursement?.reimbursements?.length ?? 0;
    if (reimbursementCount > 0) {
      alertSignal.update({
        type: 'notification',
        message: 'Cannot delete a disbursement that has related reimbursements.',
        variant: 'danger',
      });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this disbursement? This action cannot be undone.')) return;
    loaderSignal.update({ isContentLoading: true, message: 'Deleting disbursement...' });
    try {
      await api.delete({ path: `/v2/disbursements/${disbursement.id}` });
      await fetchApplication(applicationId);
      alertSignal.update({ type: 'notification', message: 'Disbursement deleted.', variant: 'success' });
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        message: error?.message ?? 'Failed to delete disbursement.',
        variant: 'danger',
        error,
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const handleDeleteTransaction = async (transaction) => {
    if (!applicationId) return;
    const attachments = Array.isArray(transaction?.attachments) ? transaction.attachments : [];
    const hasAttachments = attachments.length > 0;
    const confirmMessage = hasAttachments
      ? 'Are you sure you want to delete this transaction? Any attached files will also be permanently deleted. This action cannot be undone.'
      : 'Are you sure you want to delete this transaction? This action cannot be undone.';
    if (!window.confirm(confirmMessage)) return;
    loaderSignal.update({ isContentLoading: true, message: 'Deleting transaction...' });
    try {
      await api.delete({ path: `/v2/transactions/${transaction.id}` });
      await fetchApplication(applicationId);
      alertSignal.update({ type: 'notification', message: 'Transaction deleted.', variant: 'success' });
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        message: error?.message ?? 'Failed to delete transaction.',
        variant: 'danger',
        error,
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const renderTransactionsTable = (transactions) => (
    <>
      <h6 className="mb-2 mt-2 transactions-table-label">Transactions</h6>
      <Table responsive className="mb-0 application-disbursements-tab">
      <thead>
        <tr>
          {TRANSACTION_TABLE_HEADERS.map((h) => (
            <th key={h.key} className={h.key === 'actions' ? 'text-right' : ''}>{h.title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transactions.map((transaction) => {
          const attachments = Array.isArray(transaction?.attachments) ? transaction.attachments : [];
          return (
            <tr key={transaction.id} className="application-disbursements-transaction-row">
              <td>{transaction.id}</td>
              <td>{(transaction?.amount || 0).toLocaleString('en-US', currencyOptions.currencyUS)}</td>
              <td>{formatDateTime(transaction?.createdDate ?? transaction?.transferredDate)}</td>
              <td>{transaction?.memo ?? '-'}</td>
              <td>
                {attachments.length > 0 ? (
                  <span className="d-flex flex-column">
                    {attachments.map((att, i) => (
                      <FilePreviewButton
                        key={i}
                        render={(_, fn) => (
                          <Button
                            variant="link"
                            className="p-0 mb-1 align-baseline text-primary text-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              fn(
                                {
                                  parentId: transaction.id,
                                  attachmentIndex: i,
                                  fileType: att.fileType,
                                },
                                'transactionDocuments',
                                att.fileType
                              );
                            }}
                          >
                            {att.fileTitle ?? att.name ?? `Attachment ${i + 1}`}
                          </Button>
                        )}
                      />
                    ))}
                  </span>
                ) : '-'}
              </td>
              <td className="text-right">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="rounded mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditTransactionModal(transaction);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTransaction(transaction);
                  }}
                >
                  Delete
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
    </>
  );

  const mainRows = [];
  disbursementsList.forEach((disbursement) => {
    const transactionsSum = (disbursement?.transactions || [])
      .reduce((acc, t) => acc + t.amount, 0);
    const status =
      transactionsSum === (disbursement?.amount ?? 0) ? 'Fulfilled' : 'Queued';
    const disbursementTransactionCount = disbursement?.transactions?.length ?? 0;
    const isDisbursementExpanded = expandedDisbursementIds.has(disbursement.id);
    mainRows.push({
      _rowType: 'disbursement',
      expand: disbursementTransactionCount > 0 ? (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); toggleDisbursementExpanded(disbursement.id); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDisbursementExpanded(disbursement.id); } }}
          style={{ cursor: 'pointer' }}
          aria-label={isDisbursementExpanded ? 'Collapse transactions' : 'Expand transactions'}
          title="Click to expand/collapse related Transactions"
        >
          <FontAwesomeIcon icon={isDisbursementExpanded ? faChevronDown : faChevronRight} />
        </span>
      ) : <span style={{ display: 'inline-block', width: '1rem' }} />,
      id: disbursement.id,
      amount: (disbursement?.amount || 0).toLocaleString('en-US', currencyOptions.currencyUS),
      fundType: disbursement?.meta?.fundType ?? '-',
      kind: disbursement?.meta?.kind ?? 'Disbursement',
      premiumRate: readPremiumRate(disbursement),
      status,
      transactions: `${disbursement?.transactions?.length ?? 0} (${transactionsSum.toLocaleString('en-US', currencyOptions.currencyUS)})`,
      created: formatDate(disbursement?.disbursementCreationDate ?? disbursement?.createdDate),
      premiumDate: formatDate(readPremiumDate(disbursement)),
      actions: (
        <>
          <Button
            size="sm"
            variant="outline-primary"
            className="rounded mr-1"
            onClick={(e) => {
              e.stopPropagation();
              openAddTransactionModal(disbursement);
            }}
          >
            Add Transaction
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="rounded mr-1"
            onClick={(e) => {
              e.stopPropagation();
              openEditDisbursementModal(disbursement);
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            className="rounded"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteDisbursement(disbursement);
            }}
          >
            Delete
          </Button>
        </>
      ),
      _disbursement: disbursement,
      _isExpanded: isDisbursementExpanded,
    });
    (disbursement?.reimbursements || []).forEach((reimbursement) => {
      const reimbTransactionsSum = (reimbursement?.transactions || [])
        .reduce((acc, t) => acc + t.amount, 0);
      const reimbTransactionCount = reimbursement?.transactions?.length ?? 0;
      const isReimbursementExpanded = expandedReimbursementIds.has(reimbursement.id);
      mainRows.push({
        _rowType: 'reimbursement',
        expand: reimbTransactionCount > 0 ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); toggleReimbursementExpanded(reimbursement.id); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleReimbursementExpanded(reimbursement.id); } }}
            style={{ cursor: 'pointer' }}
            aria-label={isReimbursementExpanded ? 'Collapse transactions' : 'Expand transactions'}
            title="Click to expand/collapse related Transactions"
          >
            <FontAwesomeIcon icon={isReimbursementExpanded ? faChevronDown : faChevronRight} />
          </span>
        ) : <span style={{ display: 'inline-block', width: '1rem' }} />,
        id: reimbursement.id,
        amount: (reimbursement?.amount || 0).toLocaleString('en-US', currencyOptions.currencyUS),
        fundType: '-',
        kind: 'Recycled Funds',
        premiumRate: '-',
        status: 'Fulfilled',
        transactions: `${reimbursement?.transactions?.length ?? 0} (${reimbTransactionsSum.toLocaleString('en-US', currencyOptions.currencyUS)})`,
        created: formatDate(reimbursement?.createdDate),
        premiumDate: formatDate(reimbursement?.dueDate),
        actions: '',
        _reimbursement: reimbursement,
        _isExpanded: isReimbursementExpanded,
      });
    });
  });

  const editTransactionWouldExceed = (() => {
    if (!editTransaction) return false;
    const amount = Number(editTransactionAmount);
    if (Number.isNaN(amount) || amount <= 0) return false;
    const parentInfo = findTransactionParent(disbursementsList, editTransaction.id);
    if (!parentInfo) return false;
    const otherSum = parentInfo.transactions
      .filter((t) => t.id !== editTransaction.id)
      .reduce((acc, t) => acc + t.amount, 0);
    return otherSum + amount > parentInfo.parent.amount;
  })();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Disbursements</h5>
        <Button
          variant="primary"
          className="rounded"
          onClick={openAddDisbursementModal}
          disabled={isDraftApplication}
        >
          Add Disbursement
        </Button>
      </div>
      {!disbursementsList.length ? (
        <Alert variant="info" className="mb-0">
          <p className="mb-0">
            {isDraftApplication
              ? 'No disbursements yet. They may appear here once the application is approved and disbursement data is available.'
              : 'No disbursements have been recorded for this application. Click "Add Disbursement" to create one.'}
          </p>
        </Alert>
      ) : (
        <>
          <Table responsive className="application-disbursements-tab">
            <thead>
              <tr>
                {DISBURSEMENT_TABLE_HEADERS.map((h) => (
                  <th key={h.key} className={h.key === 'actions' ? 'text-right' : ''}>{h.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mainRows.map((row) => {
                const rowKey = row._rowType === 'disbursement' ? `d-${row.id}` : `r-${row.id}`;
                const transactions = row._disbursement?.transactions || row._reimbursement?.transactions || [];
                const showTransactions = row._isExpanded && transactions.length > 0;
                return (
                  <React.Fragment key={rowKey}>
                    <tr>
                      {DISBURSEMENT_TABLE_HEADERS.map((h) => (
                        <td key={h.key} className={h.key === 'actions' ? 'text-right' : ''}>
                          {row[h.key]}
                        </td>
                      ))}
                    </tr>
                    {showTransactions && (
                      <tr>
                        <td colSpan={DISBURSEMENT_TABLE_HEADERS.length} className="pt-0 pb-0">
                          {renderTransactionsTable(transactions)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          <DisbursementAddManualTransactionModal />
              </>
            )}

      <Modal show={addDisbursementShow} onHide={hideAddDisbursementModal} onExited={resetAddDisbursementForm} centered>
        <ModalHeader>
          <ModalTitle>{editDisbursement ? 'Edit Disbursement' : 'Add Disbursement'}</ModalTitle>
          <CloseModalButton onCloseModal={hideAddDisbursementModal} />
        </ModalHeader>
        <ModalBody>
          <Form.Group className="mb-3">
            <Form.Label>Amount (USD)</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="text"
                value={
                  addDisbursementAmountFocused
                    ? addDisbursementAmountRaw
                    : (() => {
                        const num = addDisbursementAmount === '' ? null : Number(addDisbursementAmount);
                        if (num != null && !Number.isNaN(num) && Number.isFinite(num)) {
                          return new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(num);
                        }
                        return '';
                      })()
                }
                onFocus={() => {
                  setAddDisbursementAmountFocused(true);
                  const num = addDisbursementAmount === '' ? null : Number(addDisbursementAmount);
                  setAddDisbursementAmountRaw(
                    num != null && !Number.isNaN(num)
                      ? num.toFixed(2)
                      : ''
                  );
                }}
                onBlur={() => {
                  setAddDisbursementAmountFocused(false);
                  const parsed = parseCurrency(addDisbursementAmountRaw);
                  setAddDisbursementAmount(parsed != null ? String(parsed) : '');
                  setAddDisbursementAmountRaw('');
                }}
                onChange={(e) => setAddDisbursementAmountRaw(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Fund Type <span className="text-danger">*</span></Form.Label>
            <Form.Select
              className="form-control"
              value={addDisbursementFundType}
              onChange={(e) => setAddDisbursementFundType(e.target.value)}
            >
              <option value="">Select an option</option>
              {fundTypeOptions.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Premium Rate (optional)</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={addDisbursementPremiumRate}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[%\s]/g, '');
                  if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                    setAddDisbursementPremiumRate(raw);
                  }
                }}
                placeholder="0.00"
                inputMode="decimal"
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <Form.Group>
            <Form.Label>Premium Date (optional)</Form.Label>
            <Form.Control
              type="date"
              value={addDisbursementDueDate}
              onChange={(e) => setAddDisbursementDueDate(e.target.value)}
            />
          </Form.Group>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline-secondary" className="rounded" onClick={hideAddDisbursementModal}>
            Cancel
          </Button>
          <Button
            className="rounded"
            disabled={!addDisbursementAmount || Number(addDisbursementAmount) <= 0 || !addDisbursementFundType}
            onClick={submitAddDisbursement}
          >
            {editDisbursement ? 'Save Changes' : 'Add Disbursement'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal show={!!editTransaction} onHide={hideEditTransactionModal} onExited={resetEditTransactionForm} centered size="lg">
        <ModalHeader>
          <ModalTitle>Edit Transaction</ModalTitle>
          <CloseModalButton onCloseModal={hideEditTransactionModal} />
        </ModalHeader>
        <ModalBody>
          {editTransactionWouldExceed && (
            <Alert variant="danger" className="mb-3">
              The total of all transactions cannot exceed the disbursement amount.
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Amount (USD) <span className="text-danger">*</span></Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="text"
                value={
                  editTransactionAmountFocused
                    ? editTransactionAmountRaw
                    : (() => {
                        const num = editTransactionAmount === '' ? null : Number(editTransactionAmount);
                        if (num != null && !Number.isNaN(num) && Number.isFinite(num)) {
                          return new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(num);
                        }
                        return '';
                      })()
                }
                onFocus={() => {
                  setEditTransactionAmountFocused(true);
                  const num = editTransactionAmount === '' ? null : Number(editTransactionAmount);
                  setEditTransactionAmountRaw(
                    num != null && !Number.isNaN(num)
                      ? num.toFixed(2)
                      : ''
                  );
                }}
                onBlur={() => {
                  setEditTransactionAmountFocused(false);
                  const parsed = parseCurrency(editTransactionAmountRaw);
                  setEditTransactionAmount(parsed != null ? String(parsed) : '');
                  setEditTransactionAmountRaw('');
                }}
                onChange={(e) => setEditTransactionAmountRaw(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Memo <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Enter memo"
              value={editTransactionMemo}
              onChange={(e) => setEditTransactionMemo(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Created Date <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={editTransactionDate}
              onChange={(e) => setEditTransactionDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Created Time <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="time"
              value={secondsToTimeInputValue(editTransactionTime)}
              onChange={(e) => setEditTransactionTime(timeInputValueToSeconds(e.target.value))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">
              Attachments <span className="text-muted font-weight-normal">(optional)</span>
            </Form.Label>
            <div className="mt-2">
              <AddAttachmentsButton
                multiple={true}
                onChange={addEditTransactionAttachments}
              />
            </div>
            {editTransactionAttachments.length > 0 ? (
              <div className="mt-2">
                {editTransactionAttachments.map((attachment, index) => {
                  const displayName = attachment.fileTitle ?? attachment.name ?? `Attachment ${index + 1}`;
                  const isExisting = !!attachment.filePath;
                  return (
                    <Row key={index} className="mt-1 align-items-center">
                      <Col xs={6} lg={9}>
                        <Form.Control
                          type="text"
                          placeholder="Enter attachment name"
                          value={displayName}
                          onChange={({ target: { value } }) =>
                            changeEditTransactionAttachmentName(index, value)
                          }
                        />
                      </Col>
                      <Col xs={6} lg={3} className="text-right mt-1 mt-lg-0">
                        <Button
                          size="sm"
                          className="mr-1"
                          variant="outline-danger"
                          onClick={() => removeEditTransactionAttachment(index)}
                        >
                          Remove
                        </Button>
                        {isExisting ? (
                          <FilePreviewButton
                            render={(_, fn) => (
                              <Button
                                size="sm"
                                className="rounded"
                                variant="outline-info"
                                onClick={() =>
                                  fn(
                                    {
                                      parentId: editTransaction.id,
                                      attachmentIndex: attachment._originalIndex ?? index,
                                      fileType: attachment.fileType,
                                    },
                                    'transactionDocuments',
                                    attachment.fileType
                                  )
                                }
                              >
                                Preview
                              </Button>
                            )}
                          />
                        ) : (
                          <FilePreviewButton
                            render={(_, fn) => (
                              <Button
                                size="sm"
                                disabled={!attachment.file}
                                className="rounded"
                                variant="outline-info"
                                onClick={() => fn(attachment.file, '')}
                              >
                                Preview
                              </Button>
                            )}
                          />
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </div>
            ) : null}
          </Form.Group>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline-secondary" className="rounded" onClick={hideEditTransactionModal}>
            Cancel
          </Button>
          <Button
            className="rounded"
            disabled={!editTransactionAmount || Number(editTransactionAmount) <= 0 || !editTransactionMemo?.trim() || !editTransactionDate || editTransactionWouldExceed}
            onClick={submitEditTransaction}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApplicationDisbursementsTab;
